/**
 * Хуки данных модуля «Персонал» (Tanstack Query поверх @hsc/api-client).
 *
 * Бэк ListStaff требует ?department=<uuid> — поэтому полный список
 * сотрудников отеля собирается так: тянем отделы, затем по каждому отделу
 * грузим сотрудников и склеиваем. Чистый server-state, без useEffect-фетча
 * (патент проекта).
 */
import { useMemo } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createStaffMember,
  listStaffDepartments,
  listStaffMembers,
  createStaffDepartment,
  updateStaffDepartment,
  archiveStaffDepartment,
  getStaffMember,
  updateStaffMember,
  terminateStaffMember,
  issueStaffTgBindLink,
} from '@hsc/api-client';
import type {
  StaffDepartment,
  StaffMember,
  StaffMemberCreateInput,
  StaffMemberUpdateInput,
  StaffMemberTerminateInput,
  StaffTgBindLinkResponse,
  DepartmentCreateInput,
  DepartmentUpdateInput,
} from '@hsc/types';

export const STAFF_DEPTS_KEY = ['staff', 'departments'] as const;

export function useStaffDepartments() {
  return useQuery<StaffDepartment[]>({
    queryKey: STAFF_DEPTS_KEY,
    queryFn: async () => (await listStaffDepartments()).items,
    staleTime: 60_000,
  });
}

export interface DepartmentWithStaff {
  department: StaffDepartment;
  members: StaffMember[];
}

/**
 * Сотрудники всех отделов отеля, сгруппированные по отделу.
 * `isLoading` — пока грузятся отделы или хотя бы один запрос сотрудников.
 */
export function useStaffByDepartment() {
  const deptsQuery = useStaffDepartments();
  const departments = useMemo<StaffDepartment[]>(() => deptsQuery.data ?? [], [deptsQuery.data]);

  const memberQueries = useQueries({
    queries: departments.map((d) => ({
      queryKey: ['staff', 'members', d.id] as const,
      queryFn: async () => (await listStaffMembers(d.id)).items,
      staleTime: 60_000,
      enabled: deptsQuery.isSuccess,
    })),
  });

  const groups = useMemo<DepartmentWithStaff[]>(
    () =>
      departments.map((department, i) => ({
        department,
        members: memberQueries[i]?.data ?? [],
      })),
    [departments, memberQueries],
  );

  const isLoading = deptsQuery.isLoading || memberQueries.some((q) => q.isLoading);
  const isError = deptsQuery.isError || memberQueries.some((q) => q.isError);
  const totalMembers = groups.reduce((acc, g) => acc + g.members.length, 0);

  return { groups, departments, isLoading, isError, totalMembers };
}

/** Приглашение/заведение сотрудника. После успеха инвалидирует списки. */
export function useCreateStaffMember() {
  const qc = useQueryClient();
  return useMutation<StaffMember, Error, StaffMemberCreateInput>({
    mutationFn: createStaffMember,
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['staff', 'members', vars.department_id] });
    },
  });
}

/** Карточка одного сотрудника (§7.2). */
export function useStaffMember(id: string | undefined) {
  return useQuery<StaffMember>({
    queryKey: ['staff', 'member', id] as const,
    queryFn: () => getStaffMember(id as string),
    enabled: typeof id === 'string' && id !== '',
    staleTime: 30_000,
  });
}

/** Создание отдела (§ Настройки → Отделы). */
export function useCreateDepartment() {
  const qc = useQueryClient();
  return useMutation<StaffDepartment, Error, DepartmentCreateInput>({
    mutationFn: createStaffDepartment,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: STAFF_DEPTS_KEY });
    },
  });
}

/** Переименование/правка отдела. */
export function useUpdateDepartment() {
  const qc = useQueryClient();
  return useMutation<StaffDepartment, Error, { id: string; input: DepartmentUpdateInput }>({
    mutationFn: ({ id, input }) => updateStaffDepartment(id, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: STAFF_DEPTS_KEY });
    },
  });
}

/** Архивирование отдела. */
export function useArchiveDepartment() {
  const qc = useQueryClient();
  return useMutation<void, Error, { id: string; reason?: string }>({
    mutationFn: ({ id, reason }) => archiveStaffDepartment(id, reason),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: STAFF_DEPTS_KEY });
    },
  });
}

/** Правка сотрудника (отдел/должность). */
export function useUpdateStaffMember() {
  const qc = useQueryClient();
  return useMutation<StaffMember, Error, { id: string; input: StaffMemberUpdateInput }>({
    mutationFn: ({ id, input }) => updateStaffMember(id, input),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['staff', 'member', data.id] });
      void qc.invalidateQueries({ queryKey: ['staff', 'members'] });
    },
  });
}

/**
 * Выпуск deep-link/QR для привязки Telegram-аккаунта сотрудника (#88, вход
 * через бот/Mini App). Аргумент мутации — userID (StaffMember.user_id =
 * platform.users_identity.id). Каждый вызов = новый одноразовый токен.
 */
export function useStaffTgBindLink() {
  return useMutation<StaffTgBindLinkResponse, Error, string>({
    mutationFn: (userID) => issueStaffTgBindLink(userID),
  });
}

/** Увольнение сотрудника (TerminateStaffMember). */
export function useTerminateStaffMember() {
  const qc = useQueryClient();
  return useMutation<void, Error, { id: string; input: StaffMemberTerminateInput }>({
    mutationFn: ({ id, input }) => terminateStaffMember(id, input),
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: ['staff', 'member', vars.id] });
      void qc.invalidateQueries({ queryKey: ['staff', 'members'] });
    },
  });
}
